import { Component, OnInit, OnDestroy, ViewChild, ChangeDetectorRef, HostListener, ElementRef, TemplateRef } from '@angular/core';
import { Subject } from 'rxjs';
import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import { ToastrService } from 'ngx-toastr';
import { MatDialog } from '@angular/material/dialog';
import { AlternateCapacityService } from '../../services/alternate-capacity.service';
import { AuthService } from '../../services/auth.service';
import { ErrorHandler } from '../../utils/error-handler';
import {AlternateCapacityRow,GetAlternateCapacityDetailsRequest,CascadingFilterOption,TableColumn} from '../../services/interfaces/alternate-capcity.interface';
import { exportRowsToExcel } from '../../utils/excel-export';

@Component({
  selector: 'app-alternate-capacity',
  standalone: false,
  templateUrl: './alternate-capacity.component.html',
  styleUrls: ['./alternate-capacity.component.scss']
})
export class AlternateCapacityComponent implements OnInit, OnDestroy {
  @ViewChild(CdkVirtualScrollViewport) viewport!: CdkVirtualScrollViewport;
  @ViewChild('floatingFilterPanel') floatingFilterPanel!: ElementRef;
  @ViewChild('deleteConfirmDialog') deleteConfirmDialog!: TemplateRef<any>;
  @ViewChild('exportOptionsDialog') exportOptionsDialog!: TemplateRef<any>;

  spinnerBoolean: boolean = false;
  data: AlternateCapacityRow[] = [];
  selectedRows: AlternateCapacityRow[] = [];
  showCancel: boolean = false;
  loading: boolean = false;

  // Pagination
  nextRowNumber: number | null = 0;
  hasMore: boolean = true;

  // Global dropdown options
  devAreaOptions: string[] = [];
  subDevelopmentAreaOptions: string[] = [];
  alternateCapacityPlantOptions: string[] = [];
  alternateCapacityTypeOptions: string[] = ['mcf/d', '%'];

  // Per-row filtered dropdown options
  devAreaOptionsMap: { [rowId: string]: string[] } = {};
  subDevelopmentAreaOptionsMap: { [rowId: string]: string[] } = {};
  alternateCapacityPlantOptionsMap: { [rowId: string]: string[] } = {};
  alternateCapacityTypeOptionsMap: { [rowId: string]: string[] } = {};

  // Filter-related properties
  filterColumnsData: { [key: string]: string[] } = {};
  isLoadingFilterOptions: boolean = false;
  filterSearch: { [key: string]: string } = {};
  activeFilter: string = '';
  showFloatingFilter: boolean = false;
  dropdownPosition = { top: 0, left: 0 };
  multiFilters: { [key: string]: string[] } = {};
  currentFilters: { [key: string]: string | null } = {
    devArea: null,
    alternateCapacityPlant: null,
    alternateCapacityType: null,
  };

  // Date range filters for effective date
  dateRangeFilters: {
    startDate: string | null;
    endDate: string | null;
  } = {
    startDate: null,
    endDate: null,
  };

  public dialogMessage: string = '';
  private destroy$ = new Subject<void>();

  columns: TableColumn[] = [
    { field: 'devArea', header: 'Development Area', required: true, locked: true, filterable: true, filterType: 'multi-select' },
    { field: 'alternateCapacityPlant', header: 'Alternate Capacity Plant', required: true, locked: true, filterable: true, filterType: 'multi-select' },
    { field: 'alternateCapacityType', header: 'Alternate Capacity Type', required: true, locked: true, filterable: true, filterType: 'multi-select' },
    { field: 'alternateLimitQuantity', header: 'Alternate Limit Quantity', required: true, locked: false, filterable: false, filterType: 'multi-select' },
    { field: 'effectiveDate', header: 'Effective Date', required: true, locked: false, filterable: true, filterType: 'date-range' }
  ];

  constructor(
    private alternateCapacityService: AlternateCapacityService,
    private toastr: ToastrService,
    private authService: AuthService,
    private cdr: ChangeDetectorRef,
    private elementRef: ElementRef,
    private dialog: MatDialog
  ) {}

  async ngOnInit(): Promise<void> {
    await this.loadCapacities();
    await this.loadInitialDropdowns();
    await this.loadFilterOptionsInit();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  async loadInitialDropdowns(): Promise<void> {
    try {
      const dropdownData = await this.alternateCapacityService.getAllDropdownOptions();
      
      this.devAreaOptions = dropdownData.developmentAreas || [];
      this.subDevelopmentAreaOptions = dropdownData.subDevelopmentAreas || [];
      this.alternateCapacityPlantOptions = dropdownData.facilityNames || [];
      // Hardcode the options to mcf/d and %
      this.alternateCapacityTypeOptions = ['mcf/d', '%'];

    } catch (error: any) {
      const errorMessage = error.message || 'Failed to load dropdown options';
      this.toastr.error(errorMessage, 'Error');
    }
  }

  async loadFilterOptionsInit(): Promise<void> {
    this.isLoadingFilterOptions = true;
    try {
      const response = await this.alternateCapacityService.getCascadingFilterOptions({
        developmentArea: null,
        facilityName: null,
        alternateCapacityType: null
      });

      if (response && response.length > 0) {
        response.forEach((column: CascadingFilterOption) => {
          const fieldName = this.mapColumnNameToFieldName(column.columnName);
          this.filterColumnsData[fieldName] = column.values || [];
        });
      }
    } catch (error: any) {
      const errorMessage = error.message || 'Failed to load filter options';
      this.toastr.error(errorMessage, 'Error');
    } finally {
      this.isLoadingFilterOptions = false;
    }
  }

  async reloadFilterOptions(): Promise<void> {
    this.isLoadingFilterOptions = true;
    try {
      const response = await this.alternateCapacityService.getCascadingFilterOptions({
        developmentArea: this.currentFilters['devArea'] || null,
        facilityName: this.currentFilters['alternateCapacityPlant'] || null,
        alternateCapacityType: this.currentFilters['alternateCapacityType'] || null
      });

      if (response && response.length > 0) {
        response.forEach((column: CascadingFilterOption) => {
          const fieldName = this.mapColumnNameToFieldName(column.columnName);
          this.filterColumnsData[fieldName] = column.values || [];
        });
      }
    } catch (error: any) {
      const errorMessage = error.message || 'Failed to reload filter options';
      this.toastr.error(errorMessage, 'Error');
    } finally {
      this.isLoadingFilterOptions = false;
    }
  }

  private mapColumnNameToFieldName(columnName: string): string {
    const columnMap: { [key: string]: string } = {
      'AlternateCapacityType': 'alternateCapacityType',
      'DevelopmentArea': 'devArea',
      'FacilityName': 'alternateCapacityPlant'
    };
    return columnMap[columnName] || columnName;
  }

  async loadCapacities(filters?: Partial<GetAlternateCapacityDetailsRequest>): Promise<void> {
    try {
      this.spinnerBoolean = true;

      const filterPayload: GetAlternateCapacityDetailsRequest = {
        developmentArea: this.currentFilters['devArea'] || null,
        facilityName: this.currentFilters['alternateCapacityPlant'] || null,
        alternateCapacityType: this.currentFilters['alternateCapacityType'] || null,
        startDate: this.dateRangeFilters.startDate || null,
        endDate: this.dateRangeFilters.endDate || null,
        lastRowNumber: filters?.lastRowNumber ?? 0
      };

      const response = await this.alternateCapacityService.getCapacitiesWithPagination(filterPayload);

      this.nextRowNumber = response.nextRowNumber ?? null;
      this.hasMore = this.nextRowNumber !== null && this.nextRowNumber > 0;

      if (response.capacities && response.capacities.length > 0) {
        for (const row of response.capacities) {
          (row as any)._rowId = this.generateRowId();
          row.original = { ...row };
          
          this.devAreaOptionsMap[(row as any)._rowId] = [...this.devAreaOptions];
          this.subDevelopmentAreaOptionsMap[(row as any)._rowId] = [...this.subDevelopmentAreaOptions];
          this.alternateCapacityPlantOptionsMap[(row as any)._rowId] = [...this.alternateCapacityPlantOptions];
          this.alternateCapacityTypeOptionsMap[(row as any)._rowId] = [...this.alternateCapacityTypeOptions];
        }

        if (filterPayload.lastRowNumber === 0) {
          this.data = response.capacities;
        } else {
          this.data = [...this.data, ...response.capacities];
        }
      } else {
        if (filterPayload.lastRowNumber === 0) {
          this.data = [];
          if (!this.hasAnyFilterApplied()) {
            this.toastr.info('No existing capacity data found', 'Info');
          }
        }
      }
    } catch (error: any) {
      const errorMessage = error.message || 'Failed to load capacities';
      this.toastr.error(errorMessage, 'Error');
      this.hasMore = false;
    } finally {
      this.spinnerBoolean = false;
      this.loading = false;
    }
  }

  loadFirstPage(): void {
    this.data = [];
    this.nextRowNumber = 0;
    this.hasMore = true;
    this.loadCapacities({ lastRowNumber: 0 });
  }

  onScrolledIndexChange(index: number): void {
    const buffer = 10;
    if (
      this.hasMore &&
      !this.loading &&
      !this.spinnerBoolean &&
      index >= Math.max(0, this.data.length - buffer)
    ) {
      this.loading = true;
      this.loadCapacities({ lastRowNumber: this.nextRowNumber ?? 0 });
    }
  }

  async onDevelopmentAreaChange(row: AlternateCapacityRow, selectedArea: string): Promise<void> {
    try {
      this.markAsEdited(row);
      
      if (!selectedArea) {
        this.alternateCapacityPlantOptionsMap[(row as any)._rowId] = [...this.alternateCapacityPlantOptions];
        return;
      }

      if (!row.isNew) {
        row.alternateCapacityPlant = '';
      }

      const facilityNames = await this.alternateCapacityService.getFacilityNamesByDevelopmentArea(selectedArea);
      this.alternateCapacityPlantOptionsMap[(row as any)._rowId] = facilityNames;

      if (facilityNames.length === 0) {
        this.toastr.info(`No facilities available for development area: ${selectedArea}`, 'Info');
      }

    } catch (error: any) {
      const errorMessage = error.message || 'Failed to load filtered options';
      this.toastr.error(errorMessage, 'Error');
      this.alternateCapacityPlantOptionsMap[(row as any)._rowId] = [];
    }
  }

  async onFacilityNameChange(row: AlternateCapacityRow, selectedFacility: string): Promise<void> {
    try {
      this.markAsEdited(row);
      const currentArea = row.devArea;
      
      if (!selectedFacility) {
        this.devAreaOptionsMap[(row as any)._rowId] = [...this.devAreaOptions];
        this.subDevelopmentAreaOptionsMap[(row as any)._rowId] = [...this.subDevelopmentAreaOptions];
        return;
      }

      const dropdownAreas = await this.alternateCapacityService.getDevelopmentAreasByFacilityName(selectedFacility);
      const rowId = (row as any)._rowId;
      this.devAreaOptionsMap[rowId] = this.withCurrentAreaPreserved(
        dropdownAreas.developmentAreas,
        currentArea,
        false
      );
      this.subDevelopmentAreaOptionsMap[rowId] = this.withCurrentAreaPreserved(
        dropdownAreas.subDevelopmentAreas,
        currentArea,
        true
      );

      const combinedAreas = [
        ...this.devAreaOptionsMap[rowId],
        ...this.subDevelopmentAreaOptionsMap[rowId]
      ];
      if (combinedAreas.length === 1 && !row.devArea) {
        row.devArea = combinedAreas[0];
      }

    } catch (error: any) {
      const errorMessage = error.message || 'Failed to load filtered options';
      this.toastr.error(errorMessage, 'Error');
      this.devAreaOptionsMap[(row as any)._rowId] = [];
    }
  }

  getDevAreaOptionsForRow(row: AlternateCapacityRow): string[] {
    return this.devAreaOptionsMap[(row as any)._rowId] || this.devAreaOptions;
  }

  getSubDevelopmentAreaOptionsForRow(row: AlternateCapacityRow): string[] {
    return this.subDevelopmentAreaOptionsMap[(row as any)._rowId] || this.subDevelopmentAreaOptions;
  }

  private withCurrentAreaPreserved(options: string[], currentArea: string, preferSubDevelopmentArea: boolean): string[] {
    const uniqueOptions = Array.from(new Set((options || []).filter(Boolean)));

    if (!currentArea || uniqueOptions.includes(currentArea)) {
      return uniqueOptions;
    }

    const isSubDevelopmentArea = this.subDevelopmentAreaOptions.includes(currentArea);
    if (isSubDevelopmentArea !== preferSubDevelopmentArea) {
      return uniqueOptions;
    }

    return [currentArea, ...uniqueOptions];
  }

  getAlternateCapacityPlantOptionsForRow(row: AlternateCapacityRow): string[] {
    return this.alternateCapacityPlantOptionsMap[(row as any)._rowId] || this.alternateCapacityPlantOptions;
  }

  getAlternateCapacityTypeOptionsForRow(row: AlternateCapacityRow): string[] {
    return this.alternateCapacityTypeOptionsMap[(row as any)._rowId] || this.alternateCapacityTypeOptions;
  }

  addRow(): void {
    const newRow: AlternateCapacityRow = {
      devArea: '',
      alternateCapacityPlant: '',
      alternateCapacityType: '',
      effectiveDate: '',
      alternateLimitQuantity: null,
      isNew: true,
      isEdited: false,
      original: {}
    };
    const rowId = this.generateRowId();
    (newRow as any)._rowId = rowId;

    this.devAreaOptionsMap[rowId] = [...this.devAreaOptions];
    this.subDevelopmentAreaOptionsMap[rowId] = [...this.subDevelopmentAreaOptions];
    this.alternateCapacityPlantOptionsMap[rowId] = [...this.alternateCapacityPlantOptions];
    this.alternateCapacityTypeOptionsMap[rowId] = [...this.alternateCapacityTypeOptions];

    this.data = [newRow, ...this.data];
    this.showCancel = true;
    this.clearSelection();

    this.cdr.detectChanges();
    setTimeout(() => {
      this.viewport?.checkViewportSize();
      this.viewport?.scrollToIndex(0, 'smooth');
    });

    this.toastr.info('New row added. Fill all fields and click Save.');
  }

  async handleSave(): Promise<void> {
    const userName = this.authService.getUserName();
    let successCount = 0;
    let failureCount = 0;
    let errorMessages: string[] = [];

    try {
      const changedRows = this.data.filter(row => row.isNew || row.isEdited);
      
      if (changedRows.length === 0) {
        this.toastr.info('No changes to save.');
        return;
      }

      const invalidRows = changedRows.filter(
        (row) => !row.devArea || !row.alternateCapacityPlant || !row.alternateCapacityType || !row.effectiveDate || row.alternateLimitQuantity === null
      );

      if (invalidRows.length > 0) {
        this.toastr.error('Please fill all required fields including Effective Date before saving.', 'Validation Error');
        return;
      }

      this.spinnerBoolean = true;

      for (const row of changedRows) {
        const action = row.isNew ? 'INSERT' : 'UPDATE';
        
        const response = await this.alternateCapacityService.saveCapacity(row, action, userName);
        const parsed = ErrorHandler.parseApiResult(response);

        if (parsed.success) {
          row.isNew = false;
          row.isEdited = false;
          row.original = { ...row };
          successCount++;
        } else {
          failureCount++;
          errorMessages.push(parsed.message || 'Unknown error occurred');
        }
      }

      await this.loadCapacities();
      await this.reloadFilterOptions();

      if (successCount > 0) {
        this.toastr.success(`${successCount} row(s) saved successfully`, 'Success');
        this.showCancel = false;
      }

      if (failureCount > 0) {
        const detailedErrorMsg = errorMessages.length > 0
          ? errorMessages.join('<br>')
          : `${failureCount} row(s) failed to save`;
        
        this.toastr.error(detailedErrorMsg, 'Save Error', {
          timeOut: 8000,
          enableHtml: true
        });
      }
    } catch (error: any) {
      const parsed = ErrorHandler.parseApiResult(error?.error || error);
      this.toastr.error(parsed.message || 'Failed to save capacities', 'Error');
    } finally {
      this.spinnerBoolean = false;
    }
  }

  async handleDelete(): Promise<void> {
    if (!this.selectedRows.length) {
      this.toastr.info('Please select rows to delete.');
      return;
    }

    this.dialogMessage = `Are you sure you want to delete ${this.selectedRows.length} row(s)? This action cannot be undone.`;

    const dialogRef = this.dialog.open(this.deleteConfirmDialog, {
      width: '400px',
      disableClose: true,
    });

    const confirmed = await dialogRef.afterClosed().toPromise();
    if (!confirmed) return;

    let successCount = 0;
    let failureCount = 0;
    let errorMessages: string[] = [];

    try {
      this.spinnerBoolean = true;

      for (const row of this.selectedRows) {
        if (row.isNew) {
          const index = this.data.indexOf(row);
          if (index > -1) {
            this.data.splice(index, 1);
          }
          successCount++;
        } else {
          const response = await this.alternateCapacityService.removeCapacity(row);
          const parsed = ErrorHandler.parseApiResult(response);

          if (parsed.success) {
            successCount++;
          } else {
            failureCount++;
            errorMessages.push(parsed.message || 'Unknown error occurred');
          }
        }
      }

      this.selectedRows.forEach(row => {
        delete this.devAreaOptionsMap[(row as any)._rowId];
        delete this.subDevelopmentAreaOptionsMap[(row as any)._rowId];
        delete this.alternateCapacityPlantOptionsMap[(row as any)._rowId];
        delete this.alternateCapacityTypeOptionsMap[(row as any)._rowId];
      });

      this.data = this.data.filter(row => !this.selectedRows.includes(row));
      this.selectedRows = [];

      await this.loadCapacities();

      if (successCount > 0) {
        this.toastr.success(`${successCount} row(s) deleted successfully.`);
      }
      if (failureCount > 0) {
        const detailedErrorMsg = errorMessages.length > 0
          ? errorMessages.join('<br>')
          : `${failureCount} row(s) failed to delete`;
        
        this.toastr.error(detailedErrorMsg, 'Delete Error', {
          timeOut: 8000,
          enableHtml: true
        });
      }
    } catch (error: any) {
      const parsed = ErrorHandler.parseApiResult(error?.error || error);
      this.toastr.error(parsed.message || 'Failed to delete capacities', 'Error');
    } finally {
      this.spinnerBoolean = false;
    }
  }

  cancelRow(): void {
    const discardedNewRows = this.data.filter((row) => row.isNew);
    this.data = this.data.filter((row) => !row.isNew);

    discardedNewRows.forEach((row) => {
      delete this.devAreaOptionsMap[(row as any)._rowId];
      delete this.subDevelopmentAreaOptionsMap[(row as any)._rowId];
      delete this.alternateCapacityPlantOptionsMap[(row as any)._rowId];
      delete this.alternateCapacityTypeOptionsMap[(row as any)._rowId];
    });
    
    this.data.forEach((row) => {
      if (row.isEdited && row.original && Object.keys(row.original).length > 0) {
        Object.assign(row, row.original);
        row.isEdited = false;
      }
    });

    this.showCancel = false;
    this.clearSelection();

    this.cdr.detectChanges();
    setTimeout(() => {
      this.viewport?.checkViewportSize();
    });

    this.toastr.info('Changes cancelled.');
  }

  clearSelection(): void {
    this.selectedRows = [];
  }

  toggleRowSelection(row: AlternateCapacityRow, ev: Event): void {
    const target = ev.target as HTMLInputElement;
    if (target.checked) {
      if (!this.selectedRows.includes(row)) this.selectedRows.push(row);
    } else {
      const index = this.selectedRows.indexOf(row);
      if (index > -1) this.selectedRows.splice(index, 1);
    }
  }

  markAsEdited(row: AlternateCapacityRow): void {
    if (!row.original || Object.keys(row.original).length === 0) {
      row.original = { ...row };
    }
    if (!row.isNew) {
      row.isEdited = true;
      this.showCancel = true;
    }
  }

  handleEdit(row: AlternateCapacityRow): void {
    this.markAsEdited(row);
  }

  validateAlternateLimit(row: AlternateCapacityRow, ev: Event): void {
    const target = ev.target as HTMLInputElement;
    const value = parseFloat(target.value);
    row.alternateLimitQuantity = isNaN(value) ? null : value;

    if (row.alternateLimitQuantity !== null) {
      if (row.alternateCapacityType === '%') {
        if (row.alternateLimitQuantity < 0) {
          row.alternateLimitQuantity = 0;
        } else if (row.alternateLimitQuantity > 100) {
          row.alternateLimitQuantity = 100;
          this.toastr.warning('Percentage cannot exceed 100%', 'Validation');
        }
      } else if (row.alternateCapacityType === 'mcf/d') {
        if (row.alternateLimitQuantity < 0) {
          row.alternateLimitQuantity = 0;
          this.toastr.warning('mcf/d value cannot be negative', 'Validation');
        }
      }
    }

    this.markAsEdited(row);
  }

  // Filter methods
  openFloatingFilter(event: MouseEvent, field: string): void {
    event.stopPropagation();

    if (this.showFloatingFilter && this.activeFilter === field) {
      this.closeFloatingFilter();
      return;
    }

    const target = event.target as HTMLElement;
    const rect = target.getBoundingClientRect();
    this.dropdownPosition = {
      top: rect.bottom - 110 + window.scrollY,
      left: rect.left - 10 + window.scrollX,
    };
    this.activeFilter = field;
    this.showFloatingFilter = true;
  }

  closeFloatingFilter(): void {
    this.showFloatingFilter = false;
    this.activeFilter = '';
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (this.showFloatingFilter) {
      const target = event.target as HTMLElement;
      const clickedInside = this.floatingFilterPanel?.nativeElement.contains(target);
      const clickedOnFilterIcon = target.classList.contains('filter-icon') ||
        target.parentElement?.classList.contains('filter-icon');

      if (!clickedInside && !clickedOnFilterIcon) {
        this.closeFloatingFilter();
      }
    }
  }

  getFilterOptions(field: string): string[] {
    if (this.filterColumnsData[field]) {
      const options = this.filterColumnsData[field] || [];
      const search = (this.filterSearch[field] || '').toLowerCase();
      const filtered = options.filter(opt => opt && String(opt).toLowerCase().includes(search));
      return filtered;
    }
    return [];
  }

  toggleMultiFilter(field: string, value: string, event: any): void {
    if (!this.multiFilters[field]) this.multiFilters[field] = [];
    const isChecked = event.target.checked;
    if (isChecked) {
      if (!this.multiFilters[field].includes(value))
        this.multiFilters[field].push(value);
    } else {
      this.multiFilters[field] = this.multiFilters[field].filter(v => v !== value);
    }
  }

  isChecked(field: string, value: string): boolean {
    return this.multiFilters[field]?.includes(value) || false;
  }

  getFilterType(field: string): 'multi-select' | 'date-range' {
    const column = this.columns.find(col => col.field === field);
    return column?.filterType || 'multi-select';
  }

  async applyFilter(field: string): Promise<void> {
    const filterType = this.getFilterType(field);

    if (filterType === 'multi-select') {
      if (this.multiFilters[field]?.length === 1) {
        this.currentFilters[field] = this.multiFilters[field][0];
      } else if (this.multiFilters[field]?.length > 1) {
        this.currentFilters[field] = this.multiFilters[field].join(',');
      } else {
        this.currentFilters[field] = null;
      }
    }

    this.showFloatingFilter = false;
    this.activeFilter = '';

    this.loadFirstPage();
    await this.reloadFilterOptions();
  }

  applyDateRangeFilter(): void {
    this.showFloatingFilter = false;
    this.activeFilter = '';
    this.loadFirstPage();
  }

  clearDateRangeFilter(): void {
    this.dateRangeFilters.startDate = null;
    this.dateRangeFilters.endDate = null;
    this.showFloatingFilter = false;
    this.activeFilter = '';
    this.loadFirstPage();
  }

  async clearFilter(field: string): Promise<void> {
    const filterType = this.getFilterType(field);

    if (filterType === 'date-range') {
      this.clearDateRangeFilter();
      return;
    }

    this.multiFilters[field] = [];
    this.filterSearch[field] = '';
    this.currentFilters[field] = null;

    this.showFloatingFilter = false;
    this.activeFilter = '';

    this.loadFirstPage();
    await this.loadFilterOptionsInit();
  }

  hasAnyFilterApplied(): boolean {
    const hasCurrentFilter = Object.values(this.currentFilters).some(value => value !== null);
    const hasDateFilter = this.dateRangeFilters.startDate !== null || this.dateRangeFilters.endDate !== null;
    return hasCurrentFilter || hasDateFilter;
  }

  async clearAllFilters(): Promise<void> {
    this.multiFilters = {};
    this.filterSearch = {};
    this.dateRangeFilters.startDate = null;
    this.dateRangeFilters.endDate = null;

    Object.keys(this.currentFilters).forEach(key => {
      this.currentFilters[key] = null;
    });

    this.activeFilter = '';
    this.showFloatingFilter = false;

    this.loadFirstPage();
    await this.loadFilterOptionsInit();
  }

  isLockedColumn(field: string): boolean {
    const column = this.columns.find(col => col.field === field);
    return column?.locked || false;
  }

  isFilterableColumn(field: string): boolean {
    const column = this.columns.find(col => col.field === field);
    return column?.filterable !== false;
  }

  isFilterActive(field: string): boolean {
    const filterType = this.getFilterType(field);
    
    if (filterType === 'multi-select') {
      return (this.multiFilters[field] || []).length > 0;
    } else if (filterType === 'date-range') {
      return this.dateRangeFilters.startDate !== null || this.dateRangeFilters.endDate !== null;
    }
    
    return false;
  }

  async exportToCSV(): Promise<void> {
    if (!this.data.length) {
      this.toastr.info('No data available to export.');
      return;
    }

    const worksheetData = this.data.map((row: AlternateCapacityRow) => {
      const exportRow: Record<string, any> = {};

      this.columns.forEach((col) => {
        const value = row[col.field];
        exportRow[col.header] = Array.isArray(value) ? value.join(', ') : value ?? '';
      });

      return exportRow;
    });

    await exportRowsToExcel(
      worksheetData,
      this.columns.map((col) => col.header),
      'AlternateCapacityData.xlsx',
      'AlternateCapacity'
    );
  }

  openExportDialog(): void {
    this.dialog.open(this.exportOptionsDialog, {
      width: '380px',
      disableClose: true,
    });
  }

  async downloadAllData(): Promise<void> {
    try {
      const { blob, fileName } = await this.alternateCapacityService.exportAllAlternateCapacitiesData();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      link.click();
      URL.revokeObjectURL(url);
      this.toastr.success('Export downloaded successfully.');
    } catch (error: any) {
      this.toastr.error(error?.message || 'Failed to download export.');
    }
  }

  generateRowId(): string {
    return `row_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }
}
